import { Injectable } from '@angular/core';
import { io, Socket } from 'socket.io-client';
import { Subject, Observable } from 'rxjs';
import { environment } from '../../environments/environment';

/**
 * Interface pour les événements de récupération de commande
 */
export interface OrderPickupEvent {
  orderId: string;
  orderNumber: string;
  timestamp: number;
  message: string;
}

/**
 * Service de gestion WebSocket pour les notifications en temps réel
 *
 * Ce service gère la connexion WebSocket avec le backend et expose
 * des observables pour écouter les événements de commande en temps réel.
 *
 * Architecture:
 * - Connexion persistante pendant toute la session utilisateur
 * - Authentification via JWT token
 * - Reconnexion automatique en cas de perte de connexion
 * - Émission d'événements via RxJS Subjects
 */
@Injectable({
  providedIn: 'root'
})
export class WebSocketService {
  private socket: Socket | null = null;
  private orderPickedUpSubject = new Subject<OrderPickupEvent>();

  /**
   * Observable pour écouter les événements de récupération de commande
   */
  public orderPickedUp$: Observable<OrderPickupEvent> = this.orderPickedUpSubject.asObservable();

  constructor() {}

  /**
   * Établit une connexion WebSocket avec le backend
   *
   * @param token - Token JWT pour l'authentification
   *
   * Configuration:
   * - Namespace: /orders
   * - Transports: WebSocket (préféré) puis polling (fallback)
   * - Reconnexion automatique: 5 tentatives avec backoff exponentiel (1-5s)
   * - Timeout: 10 secondes
   *
   * Événements écoutés:
   * - connect: Connexion établie
   * - disconnect: Déconnexion
   * - connect_error: Erreur de connexion
   * - error: Erreur générale (notamment auth failed)
   * - order:picked-up: Notification de récupération de commande
   */
  async connect(token: string): Promise<void> {
    if (!token) {
      console.warn('WebSocketService: No token provided for connection');
      return;
    }

    // Déconnecter si déjà connecté
    if (this.socket?.connected) {
      this.disconnect();
    }

    // Créer la connexion WebSocket
    const socketUrl = `${environment.apiUrl}/orders`;

    this.socket = io(socketUrl, {
      query: { token },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      timeout: 10000
    });

    // Gestionnaires d'événements de connexion
    this.socket.on('connect', () => {
      console.log('WebSocket connected:', this.socket?.id);
    });

    this.socket.on('disconnect', (reason: string) => {
      console.log('WebSocket disconnected:', reason);
      if (reason === 'io server disconnect') {
        // Le serveur a forcé la déconnexion (probablement auth failed)
        // On ne tente pas de reconnecter automatiquement
        console.warn('WebSocket disconnected by server - authentication may have failed');
      }
    });

    this.socket.on('connect_error', (error: Error) => {
      console.error('WebSocket connection error:', error.message);
    });

    this.socket.on('error', (error: any) => {
      console.error('WebSocket error:', error);
      if (error.message === 'Authentication failed') {
        // Token invalide ou expiré - déconnecter proprement
        this.disconnect();
      }
    });

    // Gestionnaire d'événements métier
    this.socket.on('order:picked-up', (data: OrderPickupEvent) => {
      console.log('WebSocket: order:picked-up event received:', data);
      this.orderPickedUpSubject.next(data);
    });
  }

  /**
   * Ferme la connexion WebSocket
   *
   * Nettoie proprement la connexion et supprime tous les listeners
   */
  disconnect(): void {
    if (this.socket) {
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
      console.log('WebSocket disconnected manually');
    }
  }

  /**
   * Vérifie si le WebSocket est actuellement connecté
   *
   * @returns true si connecté, false sinon
   */
  isConnected(): boolean {
    return this.socket?.connected ?? false;
  }

  /**
   * Retourne l'ID du socket actuellement connecté
   * Utile pour le debug
   *
   * @returns ID du socket ou null si non connecté
   */
  getSocketId(): string | null {
    return this.socket?.id ?? null;
  }
}
